import axios from "axios"
import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import { RootState } from "@/store"
import { Timezone } from "@/types/timezone"
import { Country } from "@/types/country"
import {
    Block,
    CountriesApiResponse,
    LoadingTypes,
    TimeOfUseMutations,
    TimeOfUseState,
    TimePlan,
    Season,
    TimeplansApiResponse,
    TimezonesApiResponse,
    CreateBlock,
    BlocksApiResponse,
    SeasonApiResponse,
    CreateSeason,
    CreateTimeslot,
    TimeslotApiResponse,
    CreateTimePlan,
    TimeplanStructure,
    TimeplanStructureSlot,
    TimeplanStructureBlock,
} from "@/store/time-of-use/types"

export const TimeOfUseRoutes = {
    TIMEPLANS: "/timeplans",
    BLOCKS: "/blocks",
    SEASONS: "/seasons",
    SLOTS: "/slots",
} as const

const state: TimeOfUseState = {
    timeplanList: [],
    timezoneList: [],
    countryList: [],
    blockList: [],
    seasonList: [],
    timeslotList: [],
    newTimeplan: null,
    loading: {
        [LoadingTypes.GLOBAL]: false,
        [LoadingTypes.TIMEPLAN_LIST]: false,
        [LoadingTypes.TIMEZONE_LIST]: false,
        [LoadingTypes.COUNTRY_LIST]: false,
        [LoadingTypes.BLOCK_LIST]: false,
        [LoadingTypes.SEASON_LIST]: false,
        [LoadingTypes.POST_BLOCK]: false,
        [LoadingTypes.POST_SEASON]: false,
        [LoadingTypes.POST_TIMESLOT]: false,
        [LoadingTypes.TIMESLOT_LIST]: false,
        [LoadingTypes.TIMEPLAN_STRUCTURE]: false,
    },
    selectedTimeplan: null,
    timeplanStructure: null,
}

const mutations: MutationTree<TimeOfUseState> = {
    setTimeplanList(state, timeplanList) {
        state.timeplanList = timeplanList
    },
    setTimezoneList(state, timezoneList: Timezone[]) {
        state.timezoneList = timezoneList
    },
    setCountryList(state, countryList: Country[]) {
        state.countryList = countryList
    },
    setBlockList(state, blockList: Block[]) {
        state.blockList = blockList
    },
    setSeasonList(state, seasonList: Season[]) {
        state.seasonList = seasonList
    },
    setNewTimeplan(state, newTimeplan: TimePlan) {
        state.newTimeplan = newTimeplan
    },
    setLoading(state, payload: { type: LoadingTypes; loading: boolean }) {
        state.loading = {
            ...state.loading,
            [LoadingTypes[payload.type]]: payload.loading,
        }
    },
    setSelectedTimeplan(state, timeplan: TimePlan | null) {
        state.selectedTimeplan = timeplan
    },
    setTimeplanStructure(state, timeplanStructure) {
        state.timeplanStructure = timeplanStructure
    },
}

const actions: ActionTree<TimeOfUseState, RootState> = {
    async fetchTimeplans({ commit }, companyId?: number) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.TIMEPLAN_LIST,
            loading: true,
        })

        try {
            const url =
                typeof companyId === "number"
                    ? `companies/${companyId}${TimeOfUseRoutes.TIMEPLANS}`
                    : TimeOfUseRoutes.TIMEPLANS

            const res = await axios.get<TimeplansApiResponse>(url)
            commit(TimeOfUseMutations.SET_TIMEPLAN_LIST, res.data.data)
        } catch (e) {
            console.log(e)
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.TIMEPLAN_LIST,
                loading: false,
            })
        }
    },
    async fetchTimeplan({ commit }, id: number) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.GLOBAL,
            loading: true,
        })

        try {
            const res = await axios.get(`${TimeOfUseRoutes.TIMEPLANS}/${id}`)
            commit(TimeOfUseMutations.SET_SELECTED_TIMEPLAN, res.data.data)
        } catch (e) {
            console.log(e)
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.GLOBAL,
                loading: false,
            })
        }
    },
    async fetchTimeplansByCountry({ commit }, countryId: number) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.TIMEPLAN_LIST,
            loading: true,
        })

        try {
            const url = `${TimeOfUseRoutes.TIMEPLANS}?countryId=${countryId}`

            const res = await axios.get<TimeplansApiResponse>(url)
            commit(TimeOfUseMutations.SET_TIMEPLAN_LIST, res.data.data)
        } catch (e) {
            console.log(e)
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.TIMEPLAN_LIST,
                loading: false,
            })
        }
    },
    async deleteTimeplan({ commit, dispatch }, id: number) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.TIMEPLAN_LIST,
            loading: true,
        })

        try {
            await axios.delete(`${TimeOfUseRoutes.TIMEPLANS}/${id}`)

            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.delete_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )

            commit(TimeOfUseMutations.SET_SELECTED_TIMEPLAN, null)
            dispatch("fetchTimeplans")
        } catch (e: any) {
            let message = "time_of_use.delete_error"

            if (e?.status === 409 && e.response?.data?.error?.message) {
                message = e.response?.data?.error?.message
            }

            dispatch(
                "toastMessageModule/showMessage",
                {
                    message,
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.TIMEPLAN_LIST,
                loading: false,
            })
        }
    },
    async fetchTimezones({ commit }, countryCode: string) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.TIMEZONE_LIST,
            loading: true,
        })

        try {
            const res = await axios.get<TimezonesApiResponse>(
                `/timezones?countryCode=${countryCode}`,
            )
            commit(TimeOfUseMutations.SET_TIMEZONE_LIST, res.data.data.timezones)
        } catch (e) {
            console.log(e)
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.TIMEZONE_LIST,
                loading: false,
            })
        }
    },
    async fetchCountryList({ commit }) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.COUNTRY_LIST,
            loading: true,
        })

        try {
            const res = await axios.get<CountriesApiResponse>("/countries?visible=true")
            commit(TimeOfUseMutations.SET_COUNTRY_LIST, res.data.data.countries)
        } catch (e) {
            console.log(e)
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.COUNTRY_LIST,
                loading: false,
            })
        }
    },
    async fetchBlocks({ commit }, timeplanId: number) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.BLOCK_LIST,
            loading: true,
        })

        try {
            const res = await axios.get<BlocksApiResponse>(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}${TimeOfUseRoutes.BLOCKS}`,
            )
            commit(TimeOfUseMutations.SET_BLOCK_LIST, res.data.data)
        } catch (e) {
            console.log(e)
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.BLOCK_LIST,
                loading: false,
            })
        }
    },
    async fetchSeasons({ commit }, timeplanId: number) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.SEASON_LIST,
            loading: true,
        })

        try {
            const res = await axios.get<SeasonApiResponse>(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}${TimeOfUseRoutes.SEASONS}`,
            )

            commit(TimeOfUseMutations.SET_SEASON_LIST, res.data.data || [])
        } catch (e) {
            console.log(e)
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.SEASON_LIST,
                loading: false,
            })
        }
    },
    async fetchTimeslotsByBlockId(
        { commit },
        { timeplanId, blockId }: { timeplanId: number; blockId: number },
    ) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.TIMESLOT_LIST,
            loading: true,
        })

        try {
            const res = await axios.get<TimeslotApiResponse>(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}${TimeOfUseRoutes.BLOCKS}/${blockId}${TimeOfUseRoutes.SLOTS}`,
            )

            commit(TimeOfUseMutations.SET_TIMESLOT_LIST, res.data.data)
        } catch (e) {
            console.log(e)
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.TIMESLOT_LIST,
                loading: false,
            })
        }
    },
    async fetchTimeslotsByTimeplanId({ commit }, timeplanId: number) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.TIMESLOT_LIST,
            loading: true,
        })

        try {
            const res = await axios.get<TimeslotApiResponse>(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}${TimeOfUseRoutes.SLOTS}`,
            )

            commit(TimeOfUseMutations.SET_TIMESLOT_LIST, res.data.data)
        } catch (e) {
            console.log(e)
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.TIMESLOT_LIST,
                loading: false,
            })
        }
    },
    async postSeason(
        { commit, dispatch },
        { data, timeplanId }: { data: CreateSeason; timeplanId: number },
    ) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.POST_SEASON,
            loading: true,
        })

        try {
            const res = await axios.post(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}${TimeOfUseRoutes.SEASONS}`,
                {
                    data,
                },
            )

            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )
            return res.data.data
        } catch (e) {
            console.log(e)
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.POST_SEASON,
                loading: false,
            })
        }
    },
    async putSeason(
        { commit, dispatch },
        {
            data,
            timeplanId,
            seasonId,
        }: { data: Partial<CreateSeason>; timeplanId: number; seasonId: number },
    ) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.POST_SEASON,
            loading: true,
        })

        try {
            const res = await axios.put(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}${TimeOfUseRoutes.SEASONS}/${seasonId}`,
                {
                    data,
                },
            )

            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )
            return res.data.data
        } catch (e) {
            console.log(e)
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.POST_SEASON,
                loading: false,
            })
        }
    },
    async deleteSeason(
        { commit, dispatch },
        { timeplanId, seasonId }: { timeplanId: number; seasonId: number },
    ) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.SEASON_LIST,
            loading: true,
        })

        try {
            await axios.delete(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}${TimeOfUseRoutes.SEASONS}/${seasonId}`,
            )

            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.delete_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )

            dispatch("fetchSeasons", timeplanId)
        } catch (e) {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.SEASON_LIST,
                loading: false,
            })

            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.delete_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        }
    },
    async postTimePlan({ commit, dispatch }, data: CreateTimePlan) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.GLOBAL,
            loading: true,
        })

        try {
            const res = await axios.post(TimeOfUseRoutes.TIMEPLANS, { data })
            if (res) {
                commit(TimeOfUseMutations.SET_NEW_TIMEPLAN, res.data.data)
            }
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )
        } catch (e) {
            console.log(e)
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.GLOBAL,
                loading: false,
            })
        }
    },
    async putTimePlan({ commit, dispatch }, data: TimePlan) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.GLOBAL,
            loading: true,
        })

        try {
            const res = await axios.put(`${TimeOfUseRoutes.TIMEPLANS}/${data.id}`, { data })
            if (res) {
                commit(TimeOfUseMutations.SET_SELECTED_TIMEPLAN, res.data.data)
                dispatch("fetchTimeplans")
            }
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )
        } catch (e) {
            console.log(e)
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.GLOBAL,
                loading: false,
            })
        }
    },
    async postBlock(
        { commit, dispatch },
        { data, timeplanId }: { data: CreateBlock; timeplanId: number },
    ) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.POST_BLOCK,
            loading: true,
        })

        try {
            await axios.post(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}${TimeOfUseRoutes.BLOCKS}`,
                {
                    data,
                },
            )
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )
        } catch (e) {
            console.log(e)
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.POST_BLOCK,
                loading: false,
            })
        }
    },
    async putBlock(
        { commit, dispatch },
        { data, timeplanId, blockId }: { data: CreateBlock; timeplanId: number; blockId: number },
    ) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.POST_BLOCK,
            loading: true,
        })

        try {
            await axios.put(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}${TimeOfUseRoutes.BLOCKS}/${blockId}`,
                {
                    data,
                },
            )
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )
        } catch (e) {
            console.log(e)
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.POST_BLOCK,
                loading: false,
            })
        }
    },
    async deleteBlock(
        { dispatch, commit },
        { blockId, timeplanId }: { blockId: number; timeplanId: number },
    ) {
        try {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.BLOCK_LIST,
                loading: true,
            })
            await axios.delete(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}${TimeOfUseRoutes.BLOCKS}/${blockId}`,
            )

            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.delete_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )

            dispatch("fetchBlocks", timeplanId)
        } catch (e) {
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.delete_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.BLOCK_LIST,
                loading: false,
            })
        }
    },
    async postTimeslot(
        { commit, dispatch },
        {
            data,
            timeplanId,
            blockId,
        }: { data: CreateTimeslot; timeplanId: number; blockId: number },
    ) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.POST_TIMESLOT,
            loading: true,
        })

        try {
            await axios.post(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}${TimeOfUseRoutes.BLOCKS}/${blockId}${TimeOfUseRoutes.SLOTS}`,
                { data },
            )
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )
        } catch (e) {
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.POST_TIMESLOT,
                loading: false,
            })
        }
    },
    async putTimeslot(
        { dispatch, commit },
        {
            data,
            timeplanId,
            blockId,
            timeslotId,
        }: {
            data: Partial<CreateTimeslot>
            timeplanId: number
            blockId: number
            timeslotId: number
        },
    ) {
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.POST_TIMESLOT,
            loading: true,
        })

        try {
            await axios.put(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}${TimeOfUseRoutes.BLOCKS}/${blockId}${TimeOfUseRoutes.SLOTS}/${timeslotId}`,
                { data },
            )
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )
        } catch (e) {
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.save_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.POST_TIMESLOT,
                loading: false,
            })
        }
    },
    async deleteTimeslot(
        { dispatch },
        {
            timeplanId,
            blockId,
            timeslotId,
        }: { timeplanId: number; blockId: number; timeslotId: number },
    ) {
        try {
            await axios.delete(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}${TimeOfUseRoutes.BLOCKS}/${blockId}${TimeOfUseRoutes.SLOTS}/${timeslotId}`,
            )

            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.delete_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )

            dispatch("fetchTimeplanStructure", timeplanId)
        } catch (e) {
            console.log(e)
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "time_of_use.delete_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        }
    },
    async fetchTimeplanStructure({ commit }, timeplanId: number) {
        // this request will get all timeplan data. blocks, seasons, slots etc.
        commit(TimeOfUseMutations.SET_LOADING, {
            type: LoadingTypes.TIMEPLAN_STRUCTURE,
            loading: true,
        })

        try {
            const data = await axios.get<TimeplanStructure>(
                `${TimeOfUseRoutes.TIMEPLANS}/${timeplanId}/structure`,
            )
            commit(TimeOfUseMutations.SET_TIMEPLAN_STRUCTURE, data.data)
        } catch (e) {
            console.log(e)
        } finally {
            commit(TimeOfUseMutations.SET_LOADING, {
                type: LoadingTypes.TIMEPLAN_STRUCTURE,
                loading: false,
            })
        }
    },
}

export type SlotWithBlockStructure = TimeplanStructureSlot & {
    block: TimeplanStructureBlock
}

export type SeasonWithSlots = Season & {
    slots: SlotWithBlockStructure[]
}

const getters: GetterTree<TimeOfUseState, RootState> = {
    timeplanList: ({ timeplanList }) => timeplanList,
    timezoneList: ({ timezoneList }) => timezoneList,
    countryList: (state) => state.countryList,
    blockList: ({ blockList }) => blockList,
    seasonList: ({ seasonList }) => seasonList,
    timeslotList: ({ timeslotList }) => timeslotList,
    newTimeplan: ({ newTimeplan }) => newTimeplan,
    loading: ({ loading }) => loading,
    selectedTimeplan: ({ selectedTimeplan }) => selectedTimeplan,
    timeplanStructuredSeasons: ({ timeplanStructure, seasonList }) => {
        const seasonMap = new Map<number, SeasonWithSlots>()

        seasonList.forEach((s) => seasonMap.set(s.id, { ...s, slots: [] }))

        const allSlots: SlotWithBlockStructure[] = []

        timeplanStructure?.blocks?.forEach((b) => {
            allSlots.push(
                ...(b.slots.map((slot) => {
                    return { ...slot, block: { ...b } }
                }) || []),
            )
        })

        allSlots.forEach((slot) => {
            const matchingSeason = seasonMap.get(slot.season.id)
            matchingSeason?.slots.push(slot)
        })

        seasonMap.forEach((s) => {
            s.slots.sort((a, b) => {
                return a.validFrom > b.validFrom ? 1 : -1
            })
        })

        return seasonMap
    },
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
} as Module<TimeOfUseState, RootState>
